import { pubsub } from '@services';

/**
 * @param {string} type - The type of the response to be returned. Default is 'json'.
 * @returns {Object} - The configuration object for the fetch request.
 */
function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}

async function fetchHTML(url) {
  const response = await fetch(`${url}`);
  return new DOMParser().parseFromString(await response.text(), 'text/html');
}

function getCurrentComparisonData() {
  const storageCompareData = localStorage.getItem('compare-products');
  if (!storageCompareData) return [];
  try {
    const compareData = JSON.parse(storageCompareData);
    if (!Array.isArray(compareData)) return [];
    return compareData;
  } catch (error) {
    console.error(`Something went wrong while getting current comparison data. Error: ${error}`);
    return [];
  }
}

function makeNewComparisonData(
  comparisonData,
  shouldAddProduct,
  indexOfProductInCurrentComparison,
  productData
) {
  const newData = [...comparisonData];
  if (shouldAddProduct) {
    newData.push({
      title: productData.title,
      id: productData.id,
      price: productData.price,
      productImage: productData.productImage,
      productUrl: productData.productUrl,
    });
  } else {
    newData.splice(indexOfProductInCurrentComparison, 1);
  }
  return newData;
}

function processNewComparisonData(newData, idOfChangedProduct) {
  localStorage.setItem('compare-products', JSON.stringify(newData));
  pubsub.publish('comparison:update', {
    id: idOfChangedProduct,
    newAmount: newData.length || 0,
    newComparisonItems: newData,
  });
}

function getElementDimensions(element = null) {
  if (!element) return;

  const elementDom = document.querySelector(element);
  const rect = elementDom.getBoundingClientRect();
  return rect;
}

/**
 * Get all focusable elements inside a container
 * @param {HTMLElement} container
 * @returns {HTMLElement[]}
 */
function getFocusableElements(container) {
  if (!container?.querySelectorAll) return [];

  try {
    return Array.from(
      container.querySelectorAll(
        "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
      )
    ).filter((element) => {
      try {
        for (let current = element; current; current = current.parentElement) {
          const style = getComputedStyle(current);
          if (style.display === 'none' || style.visibility === 'hidden') return false;
        }
        return true;
      } catch {
        return false;
      }
    });
  } catch {
    return [];
  }
}

/**
 * Get the index of a node in its parent
 * @param {HTMLElement} node
 * @returns {number}
 */
function getNodeIndex(node) {
  return Array.from(node.parentNode.children).indexOf(node);
}

/**
 * Detects a click outside of a given element
 * @param {HTMLElement} element - The element to detect a click outside of
 * @param {Function} callback - The callback to execute when a click is detected outside of the element
 * @returns {Function} - A function to remove the event listener
 * @example
 * this.drawer = this.querySelector('drawer');
 * onClickOutside(this.drawer, () => this.close());
 */
function onClickOutside(element, callback) {
  const handleClick = (event) => {
    if (element && !element.contains(event.target)) callback();
  };

  document.addEventListener('mousedown', handleClick);

  return () => {
    document.removeEventListener('mousedown', handleClick);
  };
}

export { fetchConfig, fetchHTML, getCurrentComparisonData, getElementDimensions, getFocusableElements, getNodeIndex, makeNewComparisonData, onClickOutside, processNewComparisonData };
